/**
 * Global uncaught-error capture (client). This is the single most valuable piece of
 * telemetry for a server-less static SPA: it is the only crash signal for errors that
 * frogger's Vue `errorCapture` does NOT see — raw `window` errors and unhandled promise
 * rejections that originate outside Vue's reactivity (event handlers, timers, bare
 * `await`s, third-party SDKs like Google Maps).
 *
 * Logs go through the ambient `frogger` logger, so they inherit the base context stamped
 * by 000.frogger-root.client.ts and beam to the external collector like every other log.
 */
/**
 * Classify a window `error` event so benign noise does not burn collector quota
 * in a fully client-beamed SPA.
 * - `chunk`: stale dynamic-import failure a redeploy self-heals (boot reloads).
 * - `benign`: harmless Chromium ResizeObserver-loop noise, or cross-origin
 *   `Script error.` / empty-message events that carry no Error object.
 * - `error`: a genuine uncaught error worth surfacing.
 */
export function classifyWindowError(
	message: string,
	hasError: boolean,
): 'chunk' | 'benign' | 'error' {
	if (message.includes('dynamically imported module')) return 'chunk';
	if (message.includes('ResizeObserver loop')) return 'benign';
	if (!hasError && (message === '' || message === 'Script error.')) return 'benign';
	return 'error';
}

export default defineNuxtPlugin({
	name: 'frogger-global-capture',
	enforce: 'pre',
	setup(nuxtApp) {
		if (!import.meta.client) return;

		const currentRoute = (): string | undefined => {
			try {
				return window.location.pathname + window.location.search;
			} catch {
				return undefined;
			}
		};

		const stringifyReason = (reason: unknown): string => {
			if (typeof reason === 'string') return reason;
			try {
				return JSON.stringify(reason);
			} catch {
				return String(reason);
			}
		};

		window.addEventListener('error', (event: ErrorEvent) => {
			const message = typeof event?.message === 'string' ? event.message : '';
			const hasError = event?.error instanceof Error;

			const ctx = {
				message,
				filename: event?.filename,
				lineno: event?.lineno,
				colno: event?.colno,
				route: currentRoute(),
				uncaught: true,
			};

			switch (classifyWindowError(message, hasError)) {
				case 'chunk':
					// Stale-chunk dynamic-import failures after a redeploy are recovered by
					// app-boot.js (it reloads the page); classify them as a warn, not a crash.
					frogger.warn('stale chunk import error (boot self-heal will reload)', ctx);
					return;
				case 'benign':
					// ResizeObserver-loop noise and cross-origin/empty events carry no
					// actionable signal; keep them at debug so they do not burn quota.
					frogger.debug('benign window error ignored', ctx);
					return;
			}

			// Pass the real Error as an arg so frogger lifts its stack into ctx.error.
			if (hasError) {
				frogger.error('uncaught client error', event.error, ctx);
			} else {
				frogger.error('uncaught client error', ctx);
			}
		});

		// Lazy route chunks that 404 during in-app navigation (e.g. after a redeploy)
		// are handled inside vue-router and may never fire a window error — hook
		// Nuxt's chunk-error signal so these failures reach telemetry too.
		nuxtApp.hook('app:chunkError', ({ error }) => {
			frogger.warn('route chunk load failed (redeploy?)', error, {
				route: currentRoute(),
			});
		});

		window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
			const reason = event?.reason;
			const ctx = { route: currentRoute(), uncaught: true };

			if (reason instanceof Error) {
				frogger.error('unhandled promise rejection', reason, ctx);
			} else {
				frogger.error('unhandled promise rejection', {
					...ctx,
					reason: stringifyReason(reason),
				});
			}
		});
	},
});
